// ─────────────────────────────────────────────────────────────────────────────
// Backward stage-move note capture
//
// Shown when a record is moved to an EARLIER stage than the one it's already
// at (including reopening a closed record) — see validateManualStageChange in
// src/pipeline-policy.js. Forward progress is self-explanatory; slipping
// backward usually isn't, so this is the one moment to say why, the same
// reasoning already applied to LostReasonModal for closing as lost. Kept as
// its own component (free text only, no reason dropdown) rather than
// overloading LostReasonModal with a mode flag.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';

interface Props {
  recordName: string;
  fromLabel: string;
  toLabel: string;
  onCancel: () => void;
  onConfirm: (moveNote: string) => void;
  isSaving?: boolean;
}

export function StageRegressionNoteModal({ recordName, fromLabel, toLabel, onCancel, onConfirm, isSaving }: Props) {
  const [moveNote, setMoveNote] = useState('');

  return (
    <Modal open onClose={onCancel} title={`Why is "${recordName}" moving back to ${toLabel}?`}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Moving from {fromLabel} back to {toLabel} is a regression — a short note here is
          recorded in the record's activity trail so it's clear later why it slipped back.
        </p>

        <label className="block">
          <span className="text-sm font-medium block mb-1">Note</span>
          <textarea
            value={moveNote}
            onChange={(e) => setMoveNote(e.target.value)}
            rows={3}
            autoFocus
            placeholder="What happened? e.g. customer went quiet, order fell through"
            className="w-full rounded-md border px-3 py-2 text-sm"
          />
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={() => onConfirm(moveNote.trim())}
            disabled={!moveNote.trim() || isSaving}
          >
            {isSaving ? 'Saving…' : 'Confirm move'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default StageRegressionNoteModal;
